import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useRouteCache } from '@/contexts/RouteCacheContext';
import { resourceRegistry, resourceRoutes } from '@/resources/resource-map';
import { Dashboard } from '@/pages/dashboard';
import { RouteProvider } from '@/contexts/RouteContext';

/**
 * MultiRouteRenderer renders the correct route based on current path
 * while maintaining component state through caching
 */
export const MultiRouteRenderer: React.FC = () => {
	const location = useLocation();
	const { cachedRoutes, cacheRoute, activateRoute, deactivateAllRoutes } = useRouteCache();

	// Check if current route is a form route that should never be cached
	const isFormRoute = (pathname: string): boolean => {
		return pathname.includes('/create') || pathname.includes('/edit/');
	};

	const matchesPathPattern = (pattern: string, pathname: string): boolean => {
		const routeParts = pattern.split('/').filter(Boolean);
		const pathParts = pathname.split('/').filter(Boolean);

		if (routeParts.length !== pathParts.length) {
			return false;
		}

		return routeParts.every((part, index) => {
			return part.startsWith(':') || part === pathParts[index];
		});
	};

	const getRouteElementForPath = (pathname: string): React.ReactNode => {
		// Handle dashboard route
		if (pathname === '/' || pathname === '/dashboard') {
			return <Dashboard />;
		}

		// Find matching route
		const matchingRoute = resourceRoutes.find(route =>
			matchesPathPattern(route.path, pathname)
		);

		return matchingRoute ? matchingRoute.element : null;
	};

	const getCurrentRouteElement = (): React.ReactNode => {
		return getRouteElementForPath(location.pathname);
	};

	const getListPathForFormRoute = (pathname: string): string | undefined => {
		for (const resource of Object.values(resourceRegistry)) {
			if (resource.create?.path && matchesPathPattern(resource.create.path, pathname)) {
				return resource.list?.path;
			}

			if (resource.edit?.path && matchesPathPattern(resource.edit.path, pathname)) {
				return resource.list?.path;
			}
		}

		return undefined;
	};

	// Cache the route when path changes
	useEffect(() => {
		const currentElement = getCurrentRouteElement();
		if (!currentElement) return;

		// NEVER cache form routes - they need fresh state every time
		const shouldNotCache = isFormRoute(location.pathname);

		if (shouldNotCache) {
			const backgroundPath = getListPathForFormRoute(location.pathname);

			if (backgroundPath) {
				const cachedBackground = cachedRoutes.get(backgroundPath);

				if (cachedBackground) {
					if (!cachedBackground.isActive) {
						activateRoute(backgroundPath);
					}
				} else {
					const backgroundElement = getRouteElementForPath(backgroundPath);
					if (backgroundElement) {
						const wrappedElement = (
							<RouteProvider>{backgroundElement}</RouteProvider>
						);
						cacheRoute(backgroundPath, wrappedElement);
					}
				}
			} else {
				// If we can't resolve a list route, fall back to hiding cached routes
				deactivateAllRoutes();
			}

			// Form routes are never cached, always render fresh
			return;
		}

		// Check if route is already cached (check via context methods)
		const existingRoute = cachedRoutes.get(location.pathname);

		if (existingRoute) {
			// Route exists in cache, activate immediately
			activateRoute(location.pathname);
		} else {
			// New route, cache it (cacheRoute automatically activates it)
			const wrappedElement = (
				<RouteProvider>{currentElement}</RouteProvider>
			);

			// Cache and activate in one call
			cacheRoute(location.pathname, wrappedElement);
		}
	// Only depend on location.pathname - stable callbacks don't need dependencies
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	const currentRouteIsCached = cachedRoutes.has(location.pathname);
	const isFormPath = isFormRoute(location.pathname);
	const shouldRenderFresh = isFormPath || !currentRouteIsCached;

	return (
		<div className="route-cache-container">
			{/* Render all cached routes */}
			{Array.from(cachedRoutes.entries()).map(([path, cachedRoute]) => {
				const isActive = cachedRoute.isActive;

				return (
					<div
						key={path}
						className={`route-cache-item ${isActive ? 'active' : 'inactive'}`}
						data-route={path}
					>
						<div
							className={`route-cache-content h-full w-full ${
								isActive ? '' : 'frozen'
							}`}
						>
							{cachedRoute.element}
						</div>
					</div>
				);
			})}

			{/* Render non-cached routes (including form routes) fresh every time */}
			{shouldRenderFresh && (
				<div className={`route-cache-item active ${isFormPath ? 'overlay' : ''}`}>
					<div
						key={location.pathname}
						className="h-full w-full"
					>
						<RouteProvider>
							{getCurrentRouteElement()}
						</RouteProvider>
					</div>
				</div>
			)}
		</div>
	);
};
